import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useCurrency } from '@/App';
import { AlertCircle, CheckCircle2, Clock, CalendarDays, DollarSign } from 'lucide-react';
import { format, isBefore, isToday, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function CreditSalesPage() {
    const [sales, setSales] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSale, setSelectedSale] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    // Refresh trigger
    const [refresh, setRefresh] = useState(0);

    const { formatPYG } = useCurrency();

    useEffect(() => {
        fetchCreditSales();
    }, [refresh]);

    const fetchCreditSales = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API}/sales/credits`);
            setSales(response.data);

            // If we have a selected sale, refresh its data too
            if (selectedSale) {
                const refreshedSale = response.data.find(s => s.id === selectedSale.id);
                if (refreshedSale) setSelectedSale(refreshedSale);
            }
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar cuentas a cobrar');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePayInstallment = async (saleId, installmentNumber) => {
        try {
            await axios.post(`${API}/sales/${saleId}/installments/${installmentNumber}/pay`);
            toast.success('Cuota pagada exitosamente');
            setRefresh(prev => prev + 1); // Trigger reload
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al procesar pago');
        }
    };

    const getStatusInfo = (installment) => {
        if (installment.status === 'paid') return { label: 'Pagado', color: 'bg-green-100 text-green-800' };

        const dueDate = new Date(installment.due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (isBefore(dueDate, today)) {
            return { label: 'Vencido', color: 'bg-red-100 text-red-800', isOverdue: true };
        } else if (isToday(dueDate)) {
            return { label: 'Vence Hoy', color: 'bg-yellow-100 text-yellow-800' };
        }
        return { label: 'Pendiente', color: 'bg-gray-100 text-gray-800' };
    };

    // Calculations for list view
    const getSaleStatus = (sale) => {
        const installments = sale.installments || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const hasOverdue = installments.some(i => i.status === 'pending' && isBefore(new Date(i.due_date), today));

        if (hasOverdue) return { label: 'Atrasado', color: 'destructive', icon: AlertCircle };
        return { label: 'Al día', color: 'secondary', icon: CheckCircle2 };
    };

    const getPendingAmount = (sale) => {
        return (sale.installments || [])
            .filter(i => i.status === 'pending')
            .reduce((sum, i) => sum + i.amount_pyg, 0);
    };

    const getNextDueDate = (sale) => {
        const pending = (sale.installments || [])
            .filter(i => i.status === 'pending')
            .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

        if (pending.length === 0) return null;
        return pending[0].due_date;
    };


    if (isLoading && sales.length === 0) {
        return <div className="p-8 text-center text-muted-foreground">Cargando cuentas...</div>;
    }

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold flex items-center gap-2" data-font="display">
                <DollarSign className="size-8 text-green-600" /> Cuentas a Cobrar
            </h1>

            <Card>
                <CardHeader>
                    <CardTitle>Listado de Ventas a Crédito</CardTitle>
                    <CardDescription>Gestione los cobros de ventas realizadas en cuotas.</CardDescription>
                </CardHeader>
                <CardContent>
                    {sales.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground border-2 border-dashed rounded-lg">
                            No hay cuentas pendientes de cobro.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Cliente</TableHead>
                                    <TableHead>Fecha Venta</TableHead>
                                    <TableHead>Total Venta</TableHead>
                                    <TableHead>Saldo Pendiente</TableHead>
                                    <TableHead>Próx. Vencimiento</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sales.map((sale) => {
                                    const status = getSaleStatus(sale);
                                    const pendingAmount = getPendingAmount(sale);
                                    const nextDate = getNextDueDate(sale);
                                    const StatusIcon = status.icon;

                                    return (
                                        <TableRow key={sale.id}>
                                            <TableCell className="font-medium">
                                                {sale.client_name}
                                                <div className="text-xs text-muted-foreground">{sale.client_ci_ruc}</div>
                                            </TableCell>
                                            <TableCell>{format(new Date(sale.created_at), 'dd/MM/yyyy')}</TableCell>
                                            <TableCell>{formatPYG(sale.total_pyg)}</TableCell>
                                            <TableCell className="font-bold text-primary">{formatPYG(pendingAmount)}</TableCell>
                                            <TableCell>
                                                {nextDate ? (
                                                    <div className="flex items-center gap-1">
                                                        <CalendarDays className="size-3 text-muted-foreground" />
                                                        {format(new Date(nextDate), 'dd/MM/yyyy')}
                                                    </div>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={status.color} className="flex w-fit items-center gap-1">
                                                    <StatusIcon className="size-3" /> {status.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setSelectedSale(sale);
                                                        setIsDetailsOpen(true);
                                                    }}
                                                >
                                                    Ver Cuotas
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle data-font="display">Detalle de Cuotas - {selectedSale?.client_name}</DialogTitle>
                        <DialogDescription>
                            Venta #{selectedSale?.id.slice(0, 8)} • Total: {formatPYG(selectedSale?.total_pyg || 0)}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedSale && (
                        <div className="mt-4">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[50px]">#</TableHead>
                                        <TableHead>Vencimiento</TableHead>
                                        <TableHead>Monto</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {selectedSale.installments.map((inst) => {
                                        const status = getStatusInfo(inst);
                                        return (
                                            <TableRow key={inst.number} className={status.isOverdue ? 'bg-red-50/50' : ''}>
                                                <TableCell className="font-medium">{inst.number}</TableCell>
                                                <TableCell>
                                                    <span className={status.isOverdue ? 'text-red-600 font-bold' : ''}>
                                                        {format(new Date(inst.due_date), 'dd MMMM yyyy', { locale: es })}
                                                    </span>
                                                </TableCell>
                                                <TableCell>{formatPYG(inst.amount_pyg)}</TableCell>
                                                <TableCell>
                                                    <Badge className={status.color} variant="outline">
                                                        {status.label}
                                                    </Badge>
                                                    {inst.paid_at && (
                                                        <div className="text-[10px] text-muted-foreground mt-1">
                                                            {format(new Date(inst.paid_at), 'dd/MM/yy')}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {inst.status === 'pending' && (
                                                        <Button
                                                            size="sm"
                                                            className="bg-green-600 hover:bg-green-700 h-8"
                                                            onClick={() => handlePayInstallment(selectedSale.id, inst.number)}
                                                        >
                                                            Pagar
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
